import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import DeleteModal from "components/DeleteModal/DeleteModal";
import Loading from "components/Loader/Loading";
import DynamicTable from "components/Tables/DynamicTable";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaHandsHelping } from "react-icons/fa";
import { adminGetAllServiceAgents, deleteUser } from "redux/features/auth/authSlice";
import Input from "shared/Input/Input";
import DashboardMenu from "../../DashboardPage/DashboardMenu";
import ServiceAgentDetailsPage from "./ServiceAgentDetailsPage";

const ListAgentsPage: FC = () => {
  const dispatch = useAppDispatch();
  const {
    services: serviceAgents,
    totalCount,
    totalPages,
    currentPage,
    loading,
    error,
  } = useAppSelector(
    (state) =>
      state?.auth?.allServiceAgents || {
        services: [],
        totalCount: 0,
        totalPages: 0,
        currentPage: 1,
        loading: "idle",
      }
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      await dispatch(adminGetAllServiceAgents({ page, searchTerm: debouncedSearchTerm }));
      setIsSearchLoading(false);
    };
    fetchData();
  }, [dispatch, page, debouncedSearchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedServiceAgent, setSelectedServiceAgent] = useState<any | null>(null);
  const [showServiceAgentDetails, setShowServiceAgentDetails] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteModalOpen = (any: any) => {
    setSelectedServiceAgent(any);
    setShowServiceAgentDetails(false);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedServiceAgent && selectedServiceAgent.User?.id !== undefined) {
      setDeleting(true);
      await dispatch(deleteUser(selectedServiceAgent.User.id));
      setDeleting(false);
      setDeleteModalOpen(false);
      await dispatch(adminGetAllServiceAgents({ page, searchTerm: debouncedSearchTerm }));
    }
  };

  const handleEyeIconClick = (any: any) => {
    setSelectedServiceAgent(any);
    setShowServiceAgentDetails(true);
    setDeleteModalOpen(false);
  };

  const columns = [
    { key: "designation", label: "Designation" },
    { key: "actions", label: "Actions" },
  ];

  const handleTableAction = (action: string, item: any) => {
    if (action === "view") {
      handleEyeIconClick(item);
    } else if (action === "delete") {
      handleDeleteModalOpen(item);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center space-x-3">
              <FaHandsHelping className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Liste Des Prestataires
              </h2>
            </div>
            <div className="relative w-full max-w-lg">
              <Input
                type="text"
                placeholder="Rechercher par désignation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                aria-label="Search agents"
              />
              <MagnifyingGlassIcon
                className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                  searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                }`}
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                  aria-label="Clear search"
                >
                  <XMarkIcon />
                </button>
              )}
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {loading === "pending" && !isSearchLoading && <Loading />}

          {error && (
            <div className="py-4 text-center text-red-600 dark:text-red-400">
              Error: {error.message || "Failed to load service agents"}
            </div>
          )}

          {loading !== "pending" && !error && (
            <>
              {deleteModalOpen && (
                <DeleteModal
                  open={deleteModalOpen}
                  handleClose={() => setDeleteModalOpen(false)}
                  handleDelete={handleDeleteConfirm}
                  category={{
                    id: selectedServiceAgent?.User?.id || 0,
                    designation: selectedServiceAgent?.designation || "",
                  }}
                />
              )}

              <DynamicTable
                columns={columns}
                data={serviceAgents}
                totalItems={totalCount || 0}
                currentPage={currentPage || 1}
                onPageChange={handlePageChange}
                onAction={handleTableAction}
                totalPages={totalPages}
                availableActions={["view", "delete"]}
              />

              {showServiceAgentDetails && !deleting && (
                <ServiceAgentDetailsPage
                  open={true}
                  handleClose={() => setShowServiceAgentDetails(false)}
                  agentData={selectedServiceAgent}
                />
              )}
            </>
          )}
        </div>
      </DashboardMenu>
    </div>
  );
};

export default ListAgentsPage;
